import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TreatmentPlanDto,
  AssignedPatientResponse,
  WeekDateRangeResponse,
  PatientResponse,
  InterventionGroupDto
} from '../../api/generated';
import { DRAWER_TRANSITION_DURATION_MS } from '../../constants/drawerConstants';
import { flattenGroups } from '../../utils/ganttUtils';
import { formatPlanWeek, resolvePlanWeek } from '../../services/planWeekService';
import { useRoomProperties } from '../../hooks/useRoomProperties';
import { usePatientAssignment } from '../../hooks/usePatientAssignment';
import { usePreferredStaffLoader } from '../../hooks/usePreferredStaffOptions';
import { formatDateStr } from '../../utils/dateUtils';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { PatientList } from './PatientList';
import { PatientDetailView, type PatientDetailViewHandle } from './PatientDetailView';
import { resolveDisplayedPatient } from './displayedPatient';
import { TreatmentTimeline } from './TreatmentTimeline';
import { DrawerShell, Modal, WarningSign } from '../UI';
import { Button } from '../UI/base/Button';
import styles from './InterventionGroupDrawer.module.css';

export type EnrichedPatient = AssignedPatientResponse & { name?: string };

interface InterventionGroupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  treatmentPlan?: TreatmentPlanDto;
  selectedGroup?: InterventionGroupDto;
  patients?: EnrichedPatient[];
  weeks?: WeekDateRangeResponse[];
  isEditMode?: boolean;
  // Shows the plan without any way to change it or its bookings: a plan carried
  // over from the previous year belongs to that year.
  isReadOnly?: boolean;
  // Set for a plan carried over from that year: the groups it holds there are
  // not part of this year's response, so the drawer says it shows only part of it.
  carriedOverFromYear?: number;
  onCapacityChange?: (capacity: number, groupIds: string[]) => void;
  onSelectGroup?: (groupId: string) => void;
  patientsById?: Record<string, PatientResponse>;
  addPatientOnOpen?: boolean;
  onAddPatientHandled?: () => void;
  locationId?: string;
  scenarioId?: string;
  onPatientRemoved?: () => void;
  onPatientAdded?: () => void;
}

export const InterventionGroupDrawer = ({
  isOpen,
  onClose,
  treatmentPlan,
  selectedGroup,
  patients = [],
  weeks = [],
  isEditMode = false,
  isReadOnly = false,
  carriedOverFromYear,
  onCapacityChange,
  onSelectGroup,
  patientsById = {},
  addPatientOnOpen,
  onAddPatientHandled,
  locationId,
  scenarioId,
  onPatientRemoved,
  onPatientAdded
}: InterventionGroupDrawerProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const patientViewRef = useRef<PatientDetailViewHandle>(null);
  const slideOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<PatientResponse | null>(null);
  const [isSliding, setIsSliding] = useState(false);
  const [initialEditing, setInitialEditing] = useState(false);

  // Was a second, hand-rolled copy of useRoomProperties' effect calling the
  // same endpoint. Consolidated onto the hook so this drawer owns a fetch that
  // can actually be retried — its own `isOpen` is `selectedTreatmentPlanId !==
  // null`, which does not change while navigating inside the drawer, so before
  // this a failure could only be escaped by closing the whole drawer.
  const { hasError: hasRoomPropertiesError, retry: retryRoomProperties } =
    useRoomProperties(isOpen);

  // This drawer renders PatientDetailsCard too, so without its own loader the Preferred staff
  // field would read as empty here no matter what the patient actually has.
  usePreferredStaffLoader(locationId ?? null, isOpen);

  const handleAddPatient = useCallback(() => {
    const emptyPatient: PatientResponse = {
      id: '',
      name: '',
      cprNumber: '',
      patientRoomNeedIds: [],
      preferredStaffIds: [],
      deleted: false
    };
    setSelectedPatient(emptyPatient);
    setInitialEditing(true);
    requestAnimationFrame(() => setIsSliding(true));
  }, []);

  const handleBackToList = useCallback(() => {
    setIsSliding(false);
    setInitialEditing(false);
    if (slideOutTimerRef.current !== null) clearTimeout(slideOutTimerRef.current);
    slideOutTimerRef.current = setTimeout(() => {
      setSelectedPatient(null);
      slideOutTimerRef.current = null;
    }, DRAWER_TRANSITION_DURATION_MS);
  }, []);

  const [pendingDiscardAction, setPendingDiscardAction] = useState<(() => void) | null>(null);

  const guardDiscard = useCallback((action: () => void) => {
    if (patientViewRef.current?.isAddDirty()) {
      setPendingDiscardAction(() => action);
    } else {
      action();
    }
  }, []);

  const handleBack = useCallback(() => {
    if (patientViewRef.current?.goBack()) return;
    guardDiscard(handleBackToList);
  }, [handleBackToList, guardDiscard]);

  const handleCloseGuarded = useCallback(() => guardDiscard(onClose), [guardDiscard, onClose]);

  useEffect(
    () => () => {
      if (slideOutTimerRef.current !== null) clearTimeout(slideOutTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedPatient(null);
      setIsSliding(false);
    }
  }, [isOpen]);

  // The patient list can be scrolled when a patient view slides in over it;
  // without a reset the patient view opens mid-scroll with its top fields
  // (e.g. the add-patient name/CPR search) hidden above the fold.
  const isPatientViewOpen = selectedPatient !== null;
  useEffect(() => {
    if (isPatientViewOpen) contentRef.current?.scrollTo({ top: 0 });
  }, [isPatientViewOpen]);

  useEffect(() => {
    if (isOpen && addPatientOnOpen) {
      handleAddPatient();
      onAddPatientHandled?.();
    }
  }, [isOpen, addPatientOnOpen, onAddPatientHandled, handleAddPatient]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedPatient) {
          handleBack();
        } else {
          handleCloseGuarded();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleCloseGuarded, selectedPatient, handleBack]);

  const handlePatientClick = (patientId: string) => {
    const patient = patientsById[patientId];
    if (patient && !patient.deleted) {
      setSelectedPatient(patient);
      setInitialEditing(false);
      requestAnimationFrame(() => setIsSliding(true));
    }
  };

  // An added patient also closes the patient view it was added from, so the drawer is back on the
  // list the new seat has just appeared in.
  const handleAssigned = useCallback(() => {
    onPatientAdded?.();
    handleBackToList();
  }, [onPatientAdded, handleBackToList]);

  const { assignPatientToGroups: handleAssignPatient, removePatientFromGroups: handleRemovePatient } =
    usePatientAssignment({
      locationId,
      scenarioId,
      treatmentPlanId: treatmentPlan?.id,
      onAssigned: handleAssigned,
      onRemoved: onPatientRemoved
    });

  const flatGroups = useMemo(
    () => (treatmentPlan ? flattenGroups(treatmentPlan) : []),
    [treatmentPlan]
  );

  const timelineItems = useMemo(() => {
    let moduleCounter = 0;
    return [...flatGroups]
      .sort((a, b) => a.weekNumber - b.weekNumber || (a.order ?? 0) - (b.order ?? 0))
      .map((g) => {
        moduleCounter++;
        return {
          id: g.id ?? '',
          name: g.name ?? '',
          week: g.weekNumber,
          weekLabel: formatPlanWeek(resolvePlanWeek(g.weekNumber, weeks)),
          icon: g.icon,
          moduleLabel: `M${String(moduleCounter)}`,
          interventions: g.interventions.map((i) => ({
            id: i.id,
            name: i.name,
            color: i.color
          }))
        };
      });
  }, [flatGroups, weeks]);

  const selectedGroupHeader = useMemo(() => {
    if (!selectedGroup) return { title: '', dateLine: '' };
    const idx = timelineItems.findIndex((item) => item.id === selectedGroup.id);
    const ordinal = idx >= 0 ? idx + 1 : null;
    const fallbackLabel = ordinal !== null ? `M${String(ordinal)}` : t('treatment-plan-label');
    const title = selectedGroup.name?.trim() ? selectedGroup.name : fallbackLabel;
    const flat = flatGroups.find((g) => g.id === selectedGroup.id);
    const week = flat ? resolvePlanWeek(flat.weekNumber, weeks) : undefined;
    const dateLine = week
      ? `${t('week-label')} ${formatPlanWeek(week)} · ${formatDateStr(week.startDate)}`
      : '';
    return { title, dateLine };
  }, [selectedGroup, timelineItems, flatGroups, weeks, t]);

  const weekRange = useMemo(() => {
    if (flatGroups.length === 0) return '';
    const weekNums = flatGroups.map((g) => g.weekNumber).filter((w) => w > 0);
    if (weekNums.length === 0) return '';
    const minWeek = resolvePlanWeek(Math.min(...weekNums), weeks);
    const maxWeek = resolvePlanWeek(Math.max(...weekNums), weeks);
    const prefix = t('week-label');
    if (minWeek.weekNumber === maxWeek.weekNumber && minWeek.year === maxWeek.year) {
      return `${prefix} ${formatPlanWeek(minWeek)}`;
    }
    if (maxWeek.year !== minWeek.year) {
      return `${prefix} ${formatPlanWeek(minWeek)} – ${prefix} ${formatPlanWeek(maxWeek)}`;
    }
    return `${prefix} ${String(minWeek.weekNumber)}-${String(maxWeek.weekNumber)}`;
  }, [flatGroups, weeks, t]);

  // Shadow patients are backend placeholders and must never surface in the UI —
  // they are represented by the aggregated available-seats row instead
  const patientList = useMemo(
    () =>
      patients
        .filter((p) => !p.shadow)
        .map((p) => ({
          id: p.patientId ?? '',
          name: p.name ?? '',
          practitioners: '',
          deleted: patientsById[p.patientId ?? '']?.deleted ?? false
        })),
    [patients, patientsById]
  );

  const assignedPatientIds = useMemo(
    () =>
      patients
        .filter((p) => !p.shadow)
        .map((p) => p.patientId)
        .filter((id): id is string => !!id),
    [patients]
  );

  const groupCapacity = selectedGroup?.capacity ?? 0;
  const treatmentPlanName = treatmentPlan?.name ?? '—';

  const displayedPatient = resolveDisplayedPatient(selectedPatient, patientsById);

  // The two variants are mutually exclusive, so both title spans carry the id
  // the shell names the dialog by.
  const header = selectedPatient ? (
    // Chevron and title are one button: the two read as a single "go back"
    // affordance, and clicking anywhere across them goes back. The title keeps
    // `titleId`, so it still names the dialog from inside the button.
    <button className={styles.backButton} onClick={handleBack} aria-label={t('go-back')}>
      <span className={styles.backIcon}>
        <ChevronIcon />
      </span>
      <span className={styles.drawerTitle} id={titleId}>
        {displayedPatient?.name || t('add-patient')}
      </span>
    </button>
  ) : (
    <>
      <span className={styles.drawerTitle} id={titleId}>
        {selectedGroupHeader.title}
      </span>
      {selectedGroupHeader.dateLine && (
        <span className={styles.drawerSubtitle}>{selectedGroupHeader.dateLine}</span>
      )}
      <span className={styles.drawerCaption}>
        {treatmentPlanName}
        {weekRange && ` · ${weekRange}`}
      </span>
    </>
  );

  return (
    <>
      <DrawerShell
        isOpen={isOpen}
        onClose={handleCloseGuarded}
        ariaLabel={t('treatment-plan-label')}
        titleId={titleId}
        header={header}
      >
        <div ref={contentRef} className={styles.content}>
          <div className={styles.slideContainer}>
            <div className={`${styles.planView} ${selectedPatient ? styles.slideOut : ''}`}>
              {carriedOverFromYear !== undefined && (
                <div className={styles.carriedOverNotice} role="note">
                  <WarningSign variant="warning" />
                  <p className={styles.carriedOverNoticeText}>
                    {t('drawer-carried-over-notice', { year: carriedOverFromYear })}
                  </p>
                </div>
              )}
              <PatientList
                key={selectedGroup?.id ?? treatmentPlan?.id}
                patients={patientList}
                capacity={groupCapacity}
                plan={treatmentPlan}
                selectedGroup={selectedGroup}
                isEditMode={isEditMode && !isReadOnly}
                onCapacityChange={onCapacityChange}
                onPatientClick={handlePatientClick}
                onAddPatient={isReadOnly ? undefined : handleAddPatient}
                onRemovePatient={isReadOnly ? undefined : handleRemovePatient}
                locationId={locationId}
                scenarioId={scenarioId}
              />
              {/* Keyed by plan so expansion state never carries over to another plan */}
              <TreatmentTimeline
                key={treatmentPlan?.id}
                items={timelineItems}
                strokeColor={treatmentPlan?.strokeColor ?? 'var(--color-graphics-graph-second)'}
                fillColor={treatmentPlan?.fillColor ?? 'var(--color-graphics-graph-second)'}
                selectedId={selectedGroup?.id}
                onItemClick={onSelectGroup}
              />
            </div>
            {displayedPatient && (
              <div className={`${styles.patientView} ${isSliding ? styles.slideIn : ''}`}>
                <PatientDetailView
                  ref={patientViewRef}
                  patient={displayedPatient}
                  treatmentPlanName={treatmentPlanName}
                  weekRange={weekRange}
                  timelineItems={timelineItems}
                  weeks={weeks}
                  strokeColor={treatmentPlan?.strokeColor ?? 'var(--color-graphics-graph-second)'}
                  fillColor={treatmentPlan?.fillColor ?? 'var(--color-graphics-graph-second)'}
                  initialEditing={initialEditing}
                  locationId={locationId}
                  plan={treatmentPlan}
                  selectedGroup={selectedGroup}
                  assignedPatientIds={assignedPatientIds}
                  hasRoomPropertiesError={hasRoomPropertiesError}
                  onRetryRoomProperties={retryRoomProperties}
                  onAssignPatient={isReadOnly ? undefined : handleAssignPatient}
                />
              </div>
            )}
          </div>
        </div>
      </DrawerShell>
      {pendingDiscardAction && (
        <Modal
          title={t('discard-changes-title')}
          onClose={() => setPendingDiscardAction(null)}
          width={420}
          footer={
            <div className={styles.discardFooter}>
              <Button type="secondary" onClick={() => setPendingDiscardAction(null)}>
                {t('discard-changes-cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  pendingDiscardAction();
                  setPendingDiscardAction(null);
                }}
              >
                {t('discard-changes-confirm')}
              </Button>
            </div>
          }
        >
          <p className={styles.discardMessage}>{t('discard-changes-message')}</p>
        </Modal>
      )}
    </>
  );
};
